import apiClient from './api.service';

export interface UserListItem {
  id: number;
  email: string;
  preferredName: string | null;
  role: 'participant' | 'contractor' | 'administrator';
  createdAt: string;
  validationLevel: 'LEVEL_1' | 'LEVEL_2' | 'LEVEL_3' | null;
  validatedAt: string | null;
  cognitoUserStatus: string | null;
  cognitoEnabled: boolean | null;
  cognitoEmailVerified: boolean | null;
}

export interface UserListResponse {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  items: UserListItem[];
}

export interface ListUsersParams {
  page?: number;
  pageSize?: number;
  search?: string;
  role?: string;
}

export interface CountByLabel {
  label: string | null;
  count: number;
}

export interface RegistrationByDay {
  date: string;
  count: number;
}

export interface RegistrationByMonth {
  month: string;
  count: number;
}

export interface UserIndicatorsResponse {
  totals: {
    participants: number;
    contractors: number;
    administrators: number;
  };
  registrationsByDay: RegistrationByDay[];
  registrationsByMonth: RegistrationByMonth[];
  byValidationLevel: CountByLabel[];
  byGender: CountByLabel[];
  byEducationLevel: CountByLabel[];
  byEconomicLevel: CountByLabel[];
  byAgeRange: CountByLabel[];
  byState: CountByLabel[];
}

export async function listUsers(params: ListUsersParams = {}): Promise<UserListResponse> {
  const response = await apiClient.get<{ data: UserListResponse }>('/api/admin/users', { params });
  return response.data.data;
}

export async function getIndicators(): Promise<UserIndicatorsResponse> {
  const response = await apiClient.get<{ data: UserIndicatorsResponse }>(
    '/api/admin/users/indicators',
  );
  return response.data.data;
}

export async function deleteUser(userId: number): Promise<void> {
  await apiClient.delete(`/api/admin/users/${userId}`);
}

export interface UserDetailsUser {
  id: number;
  email: string;
  preferredName: string | null;
  phone: string;
  role: 'participant' | 'contractor' | 'administrator';
  active: boolean;
  createdAt: string;
}

export interface ParticipantProfileDetail {
  gender: string;
  dateOfBirth: string;
  educationLevel: string;
  economicLevel: string;
  cityId: number;
  cityName: string | null;
  neighborhood: string;
  emailMarketingConsent: boolean;
  cookieMarketingConsent: boolean | null;
  lastUpdatedAt: string;
}

export interface LegalConsentDetail {
  documentType: string;
  version: string;
  acceptedAt: string;
  ipAddress: string | null;
  userAgent: string | null;
  actor: string;
}

export interface AuthProviderDetail {
  provider: 'PASSWORD' | 'GOOGLE';
  linkedAt: string;
  lastUsedAt: string | null;
}

export interface ValidationAuditEventDetail {
  eventType: string;
  actorType: string;
  correlationId: string;
  createdAt: string;
  payloadJson: unknown;
}

export interface SurveyResponseDetail {
  surveyId: number;
  submittedAt: string;
  ipAddress: string | null;
  userAgent: string | null;
  deviceFingerprint: string | null;
}

export interface LimitedList<T> {
  items: T[];
  hasMore: boolean;
}

export interface UserDetailsResponse {
  user: UserDetailsUser;
  participantProfile: ParticipantProfileDetail | null;
  legalConsents: LegalConsentDetail[];
  authProviders: AuthProviderDetail[];
  validationAuditEvents: LimitedList<ValidationAuditEventDetail>;
  surveyResponses: LimitedList<SurveyResponseDetail>;
}

export async function getUserDetails(userId: number): Promise<UserDetailsResponse> {
  const response = await apiClient.get<{ data: UserDetailsResponse }>(
    `/api/admin/users/${userId}/details`,
  );
  return response.data.data;
}
